<template>
  <div class="picking-modal-overlay" @click.self="$emit('close')">
    <div class="picking-modal-box card shadow shipping-modal-box">
      <div class="card-body">
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">Generar ticket de envío — Remisión #{{ remissionId }}</h6>
          <div class="d-flex align-items-center gap-2">
            <button
              v-if="rawResponse"
              type="button"
              class="btn btn-outline-secondary btn-sm mb-0"
              @click="showJson = !showJson"
            >
              <i class="fas fa-code me-1"></i> {{ showJson ? 'Ocultar JSON' : 'Ver JSON' }}
            </button>
            <button type="button" class="btn-close" @click="$emit('close')"></button>
          </div>
        </div>

        <div v-if="showJson" class="shipping-json mb-3">
          <pre>{{ rawResponse }}</pre>
        </div>

        <div v-if="loading" class="text-center py-5">
          <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
          <p class="mt-2 text-sm text-secondary">Calculando opciones de envío...</p>
        </div>

        <div v-else-if="error" class="text-center py-4">
          <i class="fas fa-triangle-exclamation fa-2x text-warning"></i>
          <p class="mt-2 text-sm text-secondary">{{ error }}</p>
          <button type="button" class="btn btn-success btn-sm" @click="calculate">
            <i class="fas fa-rotate me-1"></i> Reintentar
          </button>
        </div>

        <template v-else>
          <div class="shipping-destino mb-3">
            <span class="text-xs text-secondary">
              <i class="fas fa-location-dot me-1"></i>
              Destino: <b>{{ shippingAddress?.city }}</b>
              <template v-if="shippingAddress?.province"> — {{ shippingAddress.province }}</template>
              · Código DANE: <b>{{ recipientDane }}</b>
            </span>
          </div>

          <div v-if="options.length === 0" class="text-center py-4">
            <p class="text-secondary text-sm">No se encontraron opciones de envío para este destino.</p>
          </div>

          <div v-else class="shipping-options-list mb-3">
            <div
              v-for="(option, index) in options"
              :key="index"
              class="shipping-option-row"
              :class="{ 'shipping-option-row--selected': selectedIndex === index }"
              @click="selectedIndex = index"
            >
              <div class="shipping-option-carrier">
                <img
                  v-if="carrierInfo(option).image"
                  :src="carrierInfo(option).image"
                  :alt="carrierInfo(option).name"
                  class="shipping-carrier-img"
                />
                <i v-else class="fas fa-truck-fast text-secondary"></i>
                <span class="shipping-carrier-name">
                  {{ carrierInfo(option).name }}
                </span>
              </div>
              <div class="shipping-option-days text-xs text-secondary">
                {{ deliveryDaysLabel(option) }}
              </div>
              <div class="shipping-option-price text-sm font-weight-bold">
                {{ formatPrice(option.price ?? option.total) }}
              </div>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <label class="form-control-label text-xs">Método de pago</label>
              <select v-model="paymentMethod" class="form-control form-control-sm">
                <option value="">Flete pago</option>
                <option value="Contraentrega">Contraentrega + Flete</option>
                <option value="Flete Contraentrega">Flete Contraentrega</option>
              </select>
            </div>
            <div class="col-md-6 d-flex align-items-end">
              <div class="form-check">
                <input
                  id="chargeShipping"
                  v-model="chargeToCustomer"
                  type="checkbox"
                  class="form-check-input"
                />
                <label class="form-check-label text-xs" for="chargeShipping">
                  Cobrar envío al cliente
                </label>
              </div>
            </div>
          </div>

          <div class="d-flex justify-content-end gap-2 mt-3">
            <button type="button" class="btn btn-secondary btn-sm" @click="$emit('close')">
              <i class="fas fa-times me-1"></i> Cerrar
            </button>
            <button
              type="button"
              class="btn btn-success btn-sm"
              :disabled="selectedIndex === null || generating"
              @click="generateWaybill"
            >
              <i class="fas" :class="generating ? 'fa-spinner fa-spin' : 'fa-barcode'"></i>
              {{ generating ? 'Generando...' : 'Generar guía' }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import shippingService from '@/services/shipping.service';
import { resolveCityDane, isRestrictedCity } from '@/utils/daneCities';

// Ciudad de origen fija del envío (Cúcuta, código DANE 8 dígitos)
const SENDER_CITY = '54001000';
const SENDER_STATE = '54';

export default {
  name: 'ShippingModal',
  props: {
    remission: { type: Object, required: true },
  },
  emits: ['close', 'generated'],
  data() {
    return {
      loading: true,
      error: '',
      options: [],
      selectedIndex: null,
      paymentMethod: '',
      chargeToCustomer: true,
      generating: false,
      showJson: false,
      rawResponse: '',
      recipientDane: '',
      carriersMap: {},
    };
  },
  computed: {
    remissionId() {
      return this.remission?.id;
    },
    shippingAddress() {
      const r = this.remission || {};
      const oj = r.shopify_json || {};
      if (oj.shipping_address) return oj.shipping_address;
      return {
        address1: r.address,
        city: r.user?.city,
        province: r.user?.province,
        province_code: r.user?.province_code || '',
        zip: r.user?.zip,
        company: r.document,
        phone: r.phone,
      };
    },
    orderLabel() {
      const oj = this.remission?.shopify_json || {};
      return oj.name || oj.order_number || `#${this.remission?.id}`;
    },
  },
  async mounted() {
    this.loadCarriers();
    await this.init();
  },
  methods: {
    async loadCarriers() {
      // Respaldo para resolver logo/nombre de transportadora por carrier_id
      try {
        const res = await shippingService.getCarriers();
        const list = res.data?.data || [];
        const map = {};
        list.forEach((c) => {
          if (c.carrier_id) map[c.carrier_id] = c;
        });
        this.carriersMap = map;
      } catch (err) {
        console.error('Error al cargar transportadoras:', err);
      }
    },
    /**
     * Resuelve el logo y el nombre legible de la transportadora de una opción,
     * igualando el carrier_id con la tabla carriers cuando el API no trae
     * el nombre o trae el id en su lugar.
     */
    carrierInfo(option) {
      // Id de transportadora de ConEnvios: ObjectId de Mongo (24 hex)
      const ID_RE = /^[0-9a-f]{24}$/i;

      let id = option.carrier_id
        || option.carrierId
        || option.carrier?.id
        || option.carrier?._id
        || (typeof option.carrier === 'string' ? option.carrier : '')
        || '';

      let name = option.carrier_name
        || option.carrier?.name
        || option.name
        || '';

      // El API a veces trae el id como "nombre"
      if (!id && ID_RE.test(String(name).trim())) {
        id = String(name).trim();
      }
      if (ID_RE.test(String(name).trim())) {
        name = '';
      }

      const known = this.carriersMap[id] || null;

      if (!name || name === id) {
        name = known?.name || 'Transportadora';
      }

      const image = option.carrier_image
        || option.carrier?.image_url
        || option.carrier?.logo
        || known?.image_url
        || '';

      return { id, name, image };
    },
    async init() {
      const addr = this.shippingAddress || {};

      // 1. Resolver el código DANE del destino
      this.recipientDane = resolveCityDane(
        addr.city,
        addr.province,
        addr.province_code,
        addr.zip
      );

      if (!this.recipientDane) {
        await Swal.fire({
          icon: 'warning',
          title: 'Ciudad no reconocida',
          text: `No se pudo resolver el código DANE de la ciudad "${addr.city || 'desconocida'}". Verifica la dirección de envío del pedido.`,
          customClass: { container: 'swal-high-z' },
        });
        this.$emit('close');
        return;
      }

      // 2. Validar que la ciudad no esté restringida (misma ciudad de origen)
      if (isRestrictedCity(this.recipientDane)) {
        await Swal.fire({
          icon: 'warning',
          title: 'Ciudad de destino no permitida',
          text: 'El pedido tiene como destino la misma ciudad de origen del envío (Cúcuta y área metropolitana). No se puede generar la guía.',
          customClass: { container: 'swal-high-z' },
        });
        this.$emit('close');
        return;
      }

      // 3. Calcular opciones automáticamente al abrir
      await this.calculate();
    },
    buildPackageDetail() {
      const items = this.remission?.details || [];
      const description = items
        .map((d) => d.inventory?.name || d.model?.name || 'Producto')
        .join(', ') || 'Productos';

      return [{
        type: 'Box',
        package_number: '',
        description,
        weight: 1,
        quantity: 1,
        price: parseFloat(this.remission?.grand_total ?? this.remission?.total ?? 0),
        dimensions: { length: 1, width: 1, height: 1 },
      }];
    },
    async calculate() {
      this.loading = true;
      this.error = '';
      try {
        const payload = {
          shopify_order_id: this.shopifyOrderId(),
          sender_city: SENDER_CITY,
          recipient_city: this.recipientDane,
          package_detail: this.buildPackageDetail(),
          charge_shipping_to_customer: this.chargeToCustomer,
          payment_method: this.paymentMethod,
          package_type: 'Paquetes',
          notes: this.orderLabel,
          external_id: `shp-${this.orderLabel}`,
        };
        const res = await shippingService.calculateShipping(payload);
        this.options = res.data?.data || [];
        this.rawResponse = JSON.stringify(res.data, null, 2);
      } catch (err) {
        console.error('Error al calcular el envío:', err);
        this.error = err.response?.data?.message || 'No se pudieron calcular las opciones de envío.';
      } finally {
        this.loading = false;
      }
    },
    shopifyOrderId() {
      // ID de la orden en Shopify; como fallback, el id interno de la remisión
      return this.remission?.shopify_id || this.remission?.id;
    },
    deliveryDaysLabel(option) {
      const days = option.delivery_days ?? option.delivery_time ?? option.eta;
      if (days === undefined || days === null || days === '') return '—';
      return `${days} ${String(days) === '1' ? 'día' : 'días'}`;
    },
    formatPrice(value) {
      const n = parseFloat(value || 0);
      return `$${n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    async generateWaybill() {
      if (this.selectedIndex === null) return;
      const option = this.options[this.selectedIndex];
      const addr = this.shippingAddress || {};
      const receiverName = this.remission?.name
        || `${addr.first_name || ''} ${addr.last_name || ''}`.trim()
        || 'Cliente';

      const payload = {
        shopify_order_id: this.shopifyOrderId(),
        sender: {
          address: {
            street: '', // la dirección de origen se resuelve en el backend (configurable)
            city: SENDER_CITY,
            neighborhood: '',
            state: SENDER_STATE,
            country: 'CO',
          },
        },
        receiver: {
          name: receiverName,
          address: {
            street: addr.address1 || addr.street || this.remission?.address || '',
            city: this.recipientDane,
            neighborhood: '',
            state: String(this.recipientDane).substring(0, 2),
            country: addr.country_code || 'CO',
          },
          email: this.remission?.email || this.remission?.user?.email || '',
          document_type: 'CC',
          document_number: addr.company || this.remission?.document || '10000000',
          phone: addr.phone || this.remission?.phone || this.remission?.user?.phone || '',
        },
        package_type: 'Paquetes',
        package_details: this.buildPackageDetail(),
        payment_method: this.paymentMethod,
        status: 'Aceptado',
        charge_shipping_to_customer: this.chargeToCustomer,
        notes: this.orderLabel,
        external_id: `shp-${this.orderLabel}`,
        carrier_id: this.carrierInfo(option).id || undefined,
        shipping_method: this.carrierInfo(option).id || undefined,
        shipping_cost: option.price ?? option.total ?? 0,
      };

      this.generating = true;
      try {
        const res = await shippingService.generateWaybill(payload);
        const waybillResponse = res.data?.data || {};

        // Guardar el response en la orden para re-imprimir después
        this.$emit('generated', waybillResponse);

        await Swal.fire({
          icon: 'success',
          title: 'Guía generada',
          text: `Guía ${waybillResponse.id || waybillResponse.data?.id || ''} creada correctamente.`,
          timer: 2500,
          showConfirmButton: false,
          customClass: { container: 'swal-high-z' },
        });
        this.$emit('close');
      } catch (err) {
        console.error('Error al generar la guía:', err);
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'No se pudo generar la guía de envío.',
          customClass: { container: 'swal-high-z' },
        });
      } finally {
        this.generating = false;
      }
    },
  },
};
</script>

<style scoped>
.shipping-modal-box {
  width: 720px;
  max-width: 95vw;
  max-height: 85vh;
  overflow-y: auto;
}

.shipping-json {
  max-height: 200px;
  overflow-y: auto;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px;
}

.shipping-json pre {
  margin: 0;
  font-size: 11px;
  white-space: pre-wrap;
  word-break: break-word;
}

.shipping-options-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.shipping-option-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.75rem;
  border-bottom: 1px solid #f1f1f1;
  cursor: pointer;
  transition: background 0.15s ease;
}

.shipping-option-row:last-child {
  border-bottom: none;
}

.shipping-option-row:hover {
  background: #f6f9fc;
}

.shipping-option-row--selected {
  background: #e8f5e9;
  box-shadow: inset 3px 0 0 #2dce89;
}

.shipping-option-carrier {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  min-width: 0;
}

.shipping-carrier-img {
  height: 26px;
  max-width: 90px;
  object-fit: contain;
}

.shipping-carrier-name {
  font-weight: 600;
  font-size: 0.875rem;
}

.shipping-option-days {
  width: 110px;
  text-align: center;
  flex-shrink: 0;
}

.shipping-option-price {
  width: 120px;
  text-align: right;
  flex-shrink: 0;
}
</style>
